import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@adiox/api';
import { useApi, useSession } from '@adiox/ui';
import { notifications } from '../notifications/notificationStore';

export type MailboxMessage = components['schemas']['MailboxMessage'];

const KEY = ['mailbox'];

/**
 * The user's messages from the broker. Checked every minute and whenever the window comes back into focus (messages do not
 * come over the live stream).
 */
export function useMailbox() {
  const api = useApi();
  const readOnly = useSession().user?.readOnly === true;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: KEY,
    enabled: !readOnly,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/me/messages');
      if (!data) throw new Error('mailbox failed');
      return data;
    },
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
  return {
    messages: query.data?.messages ?? [],
    unread: query.data?.unread ?? 0,
    loaded: query.data !== undefined,
    failed: query.isError,
    retry: () => void query.refetch(),
    markRead: async (id: number) => {
      await api.POST('/api/v1/me/messages/{id}/read', { params: { path: { id } } });
      await refresh();
    },
    remove: async (id: number) => {
      await api.DELETE('/api/v1/me/messages/{id}', { params: { path: { id } } });
      await refresh();
    },
  };
}

/** The unread messages that were not there before: what to announce. */
export function newlyArrived(
  previous: ReadonlySet<number> | null,
  messages: readonly MailboxMessage[],
): MailboxMessage[] {
  if (previous === null) return []; // the first look at the mailbox announces nothing
  return messages.filter((m) => !m.read && !previous.has(m.id));
}

/** Announces a message that arrives while the terminal is open, with the notifications. */
export function useMailAnnouncer(): void {
  const { messages, loaded } = useMailbox();
  const seen = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (!loaded) return;
    for (const m of newlyArrived(seen.current, messages)) {
      notifications
        .getState()
        .add({ severity: 'info', title: `New message from ${m.from}`, text: m.subject, sound: 'message' });
    }
    seen.current = new Set(messages.map((m) => m.id));
  }, [loaded, messages]);
}
